import React, { useRef, useState, useCallback, memo } from 'react';
import { Download, Image, Link, Upload, Check, AlertCircle } from 'lucide-react';
import {
  exportStepsAsJSON,
  exportFrameAsPNG,
  buildShareURL,
  copyToClipboard,
  parseStepsJSON,
} from '../../../core/utils/exportShare';
import type { ExecutionStep } from '../../../core/types/rust-engine-interfaces';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExportShareToolbarProps {
  /** Steps currently loaded in the player */
  steps: ExecutionStep[];
  /** Index of the frame currently shown */
  currentStepIndex: number;
  /** ID of the selected algorithm (e.g. "bubble_sort") */
  algorithmId: string;
  /** Human-readable name for the export filename */
  algorithmName: string;
  /** Ref to the element that wraps the <canvas> for PNG capture */
  canvasContainerRef: React.RefObject<HTMLElement>;
  /** Called after a valid JSON import so the parent can replay the steps */
  onImport: (steps: ExecutionStep[], algorithmId: string) => void;
  /** Whether any steps are currently loaded */
  hasSteps: boolean;
}

// ── Feedback state type ───────────────────────────────────────────────────────

type ButtonId = 'json' | 'png' | 'share' | 'import';
type FeedbackState = { id: ButtonId; ok: boolean; msg: string } | null;

// ── Component ─────────────────────────────────────────────────────────────────

export const ExportShareToolbar: React.FC<ExportShareToolbarProps> = memo(({
  steps,
  currentStepIndex,
  algorithmId,
  algorithmName,
  canvasContainerRef,
  onImport,
  hasSteps,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showFeedback = useCallback((id: ButtonId, ok: boolean, msg: string) => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    setFeedback({ id, ok, msg });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 2200);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleExportJSON = useCallback(() => {
    if (!hasSteps) return;
    try {
      exportStepsAsJSON(steps, algorithmId, algorithmName);
      showFeedback('json', true, 'Downloaded');
    } catch (e) {
      showFeedback('json', false, 'Export failed');
    }
  }, [steps, algorithmId, algorithmName, hasSteps, showFeedback]);

  const handleExportPNG = useCallback(async () => {
    const container = canvasContainerRef.current;
    if (!container) {
      showFeedback('png', false, 'Canvas not ready');
      return;
    }
    try {
      await exportFrameAsPNG(container, algorithmId);
      showFeedback('png', true, 'Saved');
    } catch (e) {
      showFeedback('png', false, e instanceof Error ? e.message : 'Capture failed');
    }
  }, [canvasContainerRef, algorithmId, showFeedback]);

  const handleShare = useCallback(async () => {
    const url = buildShareURL({ algorithmId, stepIndex: currentStepIndex });
    const ok  = await copyToClipboard(url);
    showFeedback('share', ok, ok ? 'Link copied!' : 'Copy failed');
  }, [algorithmId, currentStepIndex, showFeedback]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Reset so the same file can be re-imported
      e.target.value = '';
      try {
        const raw      = await file.text();
        const envelope = parseStepsJSON(raw);
        onImport(envelope.steps, envelope.algorithmId);
        showFeedback('import', true, `Loaded ${envelope.steps.length} steps`);
      } catch (err) {
        showFeedback(
          'import',
          false,
          err instanceof Error ? err.message : 'Import failed',
        );
      }
    },
    [onImport, showFeedback],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="flex items-center gap-1"
      role="group"
      aria-label="Export and share options"
    >
      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        aria-hidden="true"
        onChange={handleFileChange}
      />

      <ToolbarButton
        id="json"
        label="Export steps"
        icon={<Download className="w-3 h-3" />}
        onClick={handleExportJSON}
        disabled={!hasSteps}
        feedback={feedback}
        title="Export all steps as JSON"
      />

      <ToolbarButton
        id="png"
        label="Save frame"
        icon={<Image className="w-3 h-3" />}
        onClick={handleExportPNG}
        disabled={!hasSteps}
        feedback={feedback}
        title="Save current frame as PNG"
      />

      <ToolbarButton
        id="share"
        label="Share"
        icon={<Link className="w-3 h-3" />}
        onClick={handleShare}
        disabled={!algorithmId}
        feedback={feedback}
        title="Copy share link to clipboard"
      />

      <ToolbarButton
        id="import"
        label="Import"
        icon={<Upload className="w-3 h-3" />}
        onClick={handleImportClick}
        disabled={false}
        feedback={feedback}
        title="Import previously exported steps JSON"
      />
    </div>
  );
});

// ── Sub-component ─────────────────────────────────────────────────────────────

interface ToolbarButtonProps {
  id: ButtonId;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  feedback: FeedbackState;
  title: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  id,
  label,
  icon,
  onClick,
  disabled,
  feedback,
  title,
}) => {
  const isActive = feedback?.id === id;
  const isOk     = isActive && feedback!.ok;
  const isErr    = isActive && !feedback!.ok;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={isActive ? feedback!.msg : label}
      className="relative flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        background: isOk
          ? 'rgba(16,185,129,0.15)'
          : isErr
          ? 'rgba(239,68,68,0.13)'
          : 'rgba(255,255,255,0.04)',
        border: isOk
          ? '1px solid rgba(16,185,129,0.35)'
          : isErr
          ? '1px solid rgba(239,68,68,0.30)'
          : '1px solid rgba(255,255,255,0.08)',
        color: isOk
          ? '#34d399'
          : isErr
          ? '#f87171'
          : disabled
          ? '#3f3f46'
          : '#71717a',
      }}
    >
      {isOk ? (
        <Check className="w-3 h-3" aria-hidden="true" />
      ) : isErr ? (
        <AlertCircle className="w-3 h-3" aria-hidden="true" />
      ) : (
        <span aria-hidden="true">{icon}</span>
      )}
      <span className="hidden sm:inline">
        {isActive ? feedback!.msg : label}
      </span>
    </button>
  );
};
